/**
 * shipwalk3d.mjs — ONE renderer for every walk in this estate.
 *
 * ── EXTRACTED 2026-08-20, BEFORE THERE WAS A SECOND COPY ──────────────────────────────────────
 * It was written inside the DP walk and would have been copied into the hull walk an hour later.
 * Two renderers drawing the same declared parts is the two-copies-that-drift fault this app already
 * guards against between the browser and the server engines — the fix is cheapest before the copy
 * exists, not after it has quietly diverged.
 *
 * It knows nothing about DP, or about ship type. It takes declared parts and a station, and draws
 * them. Everything that differs between a redundancy walk and a structural walk is the STATIONS,
 * which is where it belongs.
 */
/**
 * The renderer. Deliberately small: her parts, a light, a camera that moves between stations.
 * No ocean, no sky, no weather — this is a walk INSIDE her, and dp-realistic's scene builder is
 * built for the exterior view on /dp. Reusing it here would import a sea nobody is looking at.
 */
export function makeWalk(THREE, host, m3) {
  const W = host.clientWidth || 640, H = 340;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b1016);
  const cam = new THREE.PerspectiveCamera(46, W / H, 0.5, 400);
  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(W, H);
  host.appendChild(renderer.domElement);

  scene.add(new THREE.HemisphereLight(0xbcd4ff, 0x202830, 1.1));
  const key = new THREE.DirectionalLight(0xffffff, 0.85); key.position.set(40, -60, 70); scene.add(key);

  const meshes = new Map();
  for (const part of m3.parts) {
    const pos = [];
    for (const q of part.quads) { const [a, b, c, d] = q; pos.push(...a, ...b, ...c, ...a, ...c, ...d); }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.computeVertexNormals();
    const shell = part.layer === 'shell' || part.layer === 'deck';
    const mat = new THREE.MeshStandardMaterial({
      color: shell ? 0x2c3b4a : part.layer === 'equipment' ? 0x46604c : part.layer === 'bulkheads' ? 0x8a4034 : 0x5a6ab0,
      roughness: 0.85, metalness: 0.1, side: THREE.DoubleSide,
      transparent: true, opacity: shell ? 0.16 : (part.opacity ?? 0.9),
      depthWrite: !shell,
    });
    const mesh = new THREE.Mesh(g, mat);
    mesh.userData.base = mat.opacity;
    scene.add(mesh);
    meshes.set(part.id, mesh);
  }

  let raf = 0, target = { x: 0, y: 0, z: 12 }, dist = 95, spin = 0;
  const centreOf = (ids) => {
    const b = new THREE.Box3();
    let any = false;
    for (const id of ids || []) { const m = meshes.get(id); if (m) { b.expandByObject(m); any = true; } }
    if (!any) return { c: new THREE.Vector3(0, 0, 10), r: 60 };
    const c = b.getCenter(new THREE.Vector3());
    return { c, r: Math.max(8, b.getSize(new THREE.Vector3()).length() * 0.9) };
  };

  const api = {
    goTo(station, deadParts) {
      const { c, r } = centreOf(station.focus && station.focus.length ? station.focus : null);
      target = c; dist = station.focus && station.focus.length ? r + 14 : 95;
      for (const [id, m] of meshes) {
        const isDead = deadParts && deadParts.has(id);
        const focused = station.focus && station.focus.includes(id);
        m.material.opacity = isDead ? 0.08 : focused ? 1 : m.userData.base * (station.focus && station.focus.length ? 0.55 : 1);
        m.material.color.setHex(isDead ? 0x552222 : m.material.color.getHex());
        m.material.needsUpdate = true;
      }
    },
    dispose() { cancelAnimationFrame(raf); renderer.dispose(); if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement); },
  };

  const tick = () => {
    spin += 0.0016;
    cam.position.set(target.x + dist * Math.cos(spin), target.y + dist * Math.sin(spin), target.z + dist * 0.42);
    cam.up.set(0, 0, 1);
    cam.lookAt(target.x, target.y, target.z);
    renderer.render(scene, cam);
    raf = requestAnimationFrame(tick);
  };
  tick();
  api.goTo({ focus: null }, new Set());
  return api;
}
